import { Injectable } from '@angular/core';
import { CoreProvider } from '../core.provider';
import { File, Entry } from '@awesome-cordova-plugins/file/ngx';

@Injectable({
  providedIn: 'root'
})
export class FilesService {

  private core?: CoreProvider;
  public readonly baseVideoPath = 'HandballAI/BenchVideos';
  public readonly baseCsvPath = 'HandballAI/Csv';

  private readonly mimeTypeMappins = {
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    'avi': 'video/x-msvideo',
    'wmv': 'video/x-ms-wmv',
    'flv': 'video/x-flv',
    'mkv': 'video/x-matroska',
  }

  constructor(private readonly file: File) {}

  async initCore(core: CoreProvider) {
    this.core = core;
  }

  public async ensureDirectoryExists(extraPath: string | string[] = [], basePath: string = this.baseVideoPath): Promise<void> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...basePath.split('/'), ...extraPath].filter(segment => segment !== '');
    console.log('Attempting to create directory at:', segments.join('/'));

    let currentPath = '';
    for (let segment of segments) {
      currentPath += segment + '/';
      try {
        const exists = await this.file.checkDir(this.file.dataDirectory, currentPath);
        if (!exists) throw new Error('Directory does not exist');
        console.log('Directory exists:', exists);
      } catch (error) {
        console.log('Directory does not exist, creating...');
        await this.file.createDir(this.file.dataDirectory, currentPath, false);
        console.log('Directory created successfully');
      }
    }
  }

  public async saveVideoToFilesystem(blob: Blob, extraPath: string | string[] = [], orderId: string = 'ording'): Promise<{fileName: string, fullPath: string}> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...this.baseVideoPath.split('/'), ...extraPath].filter(segment => segment !== '');
    const videoPath = segments.join('/');

    const now = new Date();
    const timestamp = now.getFullYear() +
      ('0' + (now.getMonth() + 1)).slice(-2) +
      ('0' + now.getDate()).slice(-2) + '_' +
      ('0' + now.getHours()).slice(-2) +
      ('0' + now.getMinutes()).slice(-2) +
      ('0' + now.getSeconds()).slice(-2);
    // iOS MediaRecorder produces video/mp4 instead of webm; keep the extension in sync with the real content
    const extension = blob.type.includes('mp4') ? 'mp4' : blob.type.includes('quicktime') ? 'mov' : 'webm';
    const fileName = `rec_${orderId}_${timestamp}.${extension}`;

    try {
      await this.file.writeFile(this.file.dataDirectory + videoPath, fileName, blob, { replace: true });
      const fullPath = this.file.dataDirectory + videoPath + '/' + fileName;
      return { fileName, fullPath };
    } catch (error) {
      console.error('Error saving video to filesystem:', error);
      throw error;
    }
  }

  public async saveCsvToFilesystem(blob: Blob, gameLocalId: string, name: string): Promise<{fileName: string, fullPath: string}> {
    const extraPath = [gameLocalId];
    await this.ensureDirectoryExists(extraPath, this.baseCsvPath);
    const segments = [...this.baseCsvPath.split('/'), ...extraPath].filter(segment => segment !== '');
    const csvPath = segments.join('/');
    const fileName = `HAIBench_csv_${name}.csv`;
    console.log('Saving CSV to filesystem:', this.file.dataDirectory + csvPath + '/' + fileName);
    await this.file.writeFile(this.file.dataDirectory + csvPath, fileName, blob, { replace: true });
    const fullPath = this.file.dataDirectory + csvPath + '/' + fileName;
    return { fileName, fullPath };
  }

  public async getVideoFromShareFilesystem(gameLocalId: string, videoUri: string): Promise<Blob> {
    const segments = [...this.baseVideoPath.split('/'), gameLocalId].filter(segment => segment !== '');
    const videoPath = segments.join('/');
    const extension = videoUri.split('.').pop();
    const mimeType = this.mimeTypeMappins[extension as keyof typeof this.mimeTypeMappins] ?? 'application/octet-stream';

    try{
      console.log('Reading video from filesystem:', this.file.dataDirectory + videoPath + '/' + videoUri);
      const arrayBuffer = await this.file.readAsArrayBuffer(this.file.dataDirectory + videoPath, videoUri);
      return new Blob([arrayBuffer], { type: mimeType });
    } catch (error) {
      console.error('Error reading video from filesystem:', error);
      throw error;
    }
  }

  public getDataDirectory(): string {
    return this.file.dataDirectory;
  }

  /**
   * Removes a whole folder under the base path. Tolerant to the folder not existing, since callers
   * use it to clean up caches that may never have been created.
   */
  public async deleteDirectory(extraPath: string | string[] = [], basePath: string = this.baseVideoPath): Promise<void> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...basePath.split('/'), ...extraPath].filter(segment => segment !== '');
    const parentPath = segments.slice(0, -1).join('/');
    const dirName = segments[segments.length - 1];

    try {
      await this.file.removeRecursively(this.file.dataDirectory + parentPath, dirName);
      console.log('Directory removed:', segments.join('/'));
    } catch (error) {
      // NOT_FOUND is the normal case when there was nothing to clean up
      console.log('Could not remove directory (probably does not exist):', segments.join('/'));
    }
  }

  /** Names of the direct sub-folders of a base path. Used to find leftover caches from dead sessions. */
  public async listDirectoryNames(basePath: string = this.baseVideoPath): Promise<string[]> {
    const segments = basePath.split('/').filter(segment => segment !== '');
    const parentPath = segments.slice(0, -1).join('/');
    const dirName = segments[segments.length - 1];

    try {
      const entries: Entry[] = await this.file.listDir(this.file.dataDirectory + parentPath, dirName);
      return entries.filter(entry => entry.isDirectory).map(entry => entry.name);
    } catch (error) {
      return [];
    }
  }

  public async renameFile(extraPath: string | string[] = [], fileName: string, newFileName: string): Promise<void> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...this.baseVideoPath.split('/'), ...extraPath].filter(segment => segment !== '');
    const dirPath = this.file.dataDirectory + segments.join('/');
    await this.file.moveFile(dirPath, fileName, dirPath, newFileName);
  }

  /**
   * Turns a sandbox file path into something the webview can actually load (`<video src>` included).
   * cordova-plugin-ionic-webview proxies it through its local server, which is what gives us range
   * request support for seeking. On web there is no plugin, so the path is returned untouched.
   */
  public toWebViewUrl(fullPath: string): string {
    return (window as any).Ionic?.WebView?.convertFileSrc?.(fullPath) ?? fullPath;
  }

  public async fileExists(extraPath: string | string[] = [], fileName: string): Promise<boolean> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...this.baseVideoPath.split('/'), ...extraPath].filter(segment => segment !== '');
    const videoPath = segments.join('/');

    try {
      // The trailing slash matters: checkFile() resolves `path + fileName` by plain string
      // concatenation, so without it the folder and the file name get glued together
      // ('.../BenchVideos/<folder>rec_po1.webm') and this always reported false.
      const exists = await this.file.checkFile(this.file.dataDirectory + videoPath + '/', fileName);
      return exists;
    } catch (error) {
      return false;
    }
  }

  public async downloadVideoFromUrl(url: string, extraPath: string | string[] = [], fileName?: string): Promise<{fileName: string, fullPath: string}> {
    if (typeof extraPath === 'string') extraPath = [extraPath];
    const segments = [...this.baseVideoPath.split('/'), ...extraPath].filter(segment => segment !== '');
    const videoPath = segments.join('/');

    // Generate filename if not provided
    if (!fileName) {
      const now = new Date();
      const timestamp = now.getFullYear() +
        ('0' + (now.getMonth() + 1)).slice(-2) +
        ('0' + now.getDate()).slice(-2) + '_' +
        ('0' + now.getHours()).slice(-2) +
        ('0' + now.getMinutes()).slice(-2) +
        ('0' + now.getSeconds()).slice(-2);
      fileName = `downloaded_${timestamp}.webm`;
    }

    try {
      console.log('Downloading video from:', url);

      // Fetch the video file
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Convert to blob
      const blob = await response.blob();
      console.log('Video downloaded, size:', blob.size, 'bytes');

      // Save to filesystem
      await this.file.writeFile(this.file.dataDirectory + videoPath, fileName, blob, { replace: true });
      const fullPath = this.file.dataDirectory + videoPath + '/' + fileName;

      console.log('Video saved to filesystem:', fullPath);
      return { fileName, fullPath };
    } catch (error) {
      console.error('Error downloading video:', error);
      throw error;
    }
  }

}
